import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { checkoutIntentsClient } from 'app/checkout-intents/client';

export const TEAM_ROLES = ['admin', 'developer', 'finance', 'operations', 'viewer'] as const;

export type TeamRole = (typeof TEAM_ROLES)[number];

export type TeamMember = {
  id: string;
  email: string;
  roles: TeamRole[];
  isOwner: boolean;
};

type TeamMembersResponse = {
  members: TeamMember[];
};

type TeamMemberResponse = {
  member: TeamMember;
};

type AddTeamMemberParams = {
  email: string;
  roles: TeamRole[];
};

type UpdateTeamMemberRolesParams = {
  userId: string;
  roles: TeamRole[];
};

const TEAM_MEMBERS_QUERY_KEY = ['teamMembers'];

const getClient = () => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client;
};

const teamMemberPath = (userId: string) => `/admin/team/members/${encodeURIComponent(userId)}`;

export const fetchTeamMembers = async (): Promise<TeamMember[]> => {
  const response = await getClient().get<TeamMembersResponse>('/admin/team/members');
  return response.members;
};

const addTeamMember = async (params: AddTeamMemberParams): Promise<TeamMember> => {
  const response = await getClient().post<TeamMemberResponse>('/admin/team/invites', {
    body: params,
  });
  return response.member;
};

const updateTeamMemberRoles = async ({
  userId,
  roles,
}: UpdateTeamMemberRolesParams): Promise<TeamMember> => {
  const response = await getClient().patch<TeamMemberResponse>(teamMemberPath(userId), {
    body: { roles },
  });
  return response.member;
};

const removeTeamMember = async (userId: string): Promise<void> => {
  await getClient().delete<void>(teamMemberPath(userId));
};

export const useTeamMembers = () => {
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: TEAM_MEMBERS_QUERY_KEY,
    queryFn: fetchTeamMembers,
    retry: 1,
  });

  return {
    members: data ?? [],
    loading,
    error: error as Error | null,
  };
};

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addTeamMember,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY }),
  });
};

export const useUpdateTeamMemberRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTeamMemberRoles,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY }),
  });
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeTeamMember,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY }),
  });
};
